// Telemetry
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
/** Default window (ms) over which store publishes are counted per store */ const DefaultStorePublishWindowMs = 1000;
/** Default number of publishes within the window that triggers a diagnostic log */ const DefaultStorePublishThreshold = 50;
/** Returns `value` when it is a finite number `>= min`, otherwise `fallback` */ function sanitizeNumber(value, min, fallback) {
    return Number.isFinite(value) && value >= min ? value : fallback;
}
/**
 * Default implementation of {@link IStorePublishFrequencyMonitor}.
 *
 * Counts publishes per store over a fixed `windowMs` window and logs that window's total once the
 * window ends, but only if the total reached `threshold`. Windows roll over on the wall clock from
 * the publish path: each publish compares `Date.now()` against the current window start, so a
 * sustained storm is split into accurate per-`windowMs` buckets even when the event loop is
 * saturated and the flush timer is starved. A single trailing timer still flushes a partial window
 * when publishing stops mid-window (idle thread → fires on schedule).
 *
 * Hot-path cost per publish is one `Date.now()`, a `Map` lookup, and a compare — negligible next to
 * the render/subscriber work each publish already drives. The log call is wrapped so telemetry
 * failures can never break publishing.
 */ export class StorePublishFrequencyMonitor {
    get isEnabled() {
        return this.config.enabled;
    }
    recordPublish(storeName, subscriberCount) {
        if (!this.config.enabled) {
            return;
        }
        const now = Date.now();
        const windowState = this.windowByStore.get(storeName);
        if (windowState === undefined) {
            this.openWindow(storeName, subscriberCount, now);
            return;
        }
        // Wall-clock rollover: once the current window has run its full duration, close and log it
        // now (the trailing timer may be starved under a storm) and start a fresh one-count window.
        // This keeps counts bucketed per window instead of collapsing a storm into one giant window.
        if (now - windowState.windowStart >= this.config.windowMs) {
            this.closeWindow(storeName, windowState, now);
            this.openWindow(storeName, subscriberCount, now);
            return;
        }
        windowState.count++;
        windowState.lastSubscriberCount = subscriberCount;
    }
    /** Start a new counting window for the store and schedule its single trailing flush. */ openWindow(storeName, subscriberCount, now) {
        // The trailing timer only matters when publishing stops mid-window; while publishing
        // continues, recordPublish rolls the window over on the wall clock. The handle is retained
        // so a rollover and dispose() can both cancel it — a torn-down host must not leak timers or
        // log through a stale logger.
        const state = {
            windowStart: now,
            count: 1,
            lastSubscriberCount: subscriberCount,
            timer: setTimeout(()=>this.closeWindow(storeName, state, Date.now()), this.config.windowMs)
        };
        this.windowByStore.set(storeName, state);
    }
    /**
     * Close the given window: cancel its trailing timer, drop the store entry (bounding memory), and
     * emit the diagnostic if it reached the threshold.
     */ closeWindow(storeName, state, windowEnd) {
        clearTimeout(state.timer);
        this.windowByStore.delete(storeName);
        if (state.count >= this.config.threshold) {
            this.logWindowExceeded(storeName, state, windowEnd);
        }
    }
    dispose() {
        // Cancel every open window's pending flush timer so nothing fires after teardown, then drop
        // all state. Windows that already flushed are gone from the map, so only live timers remain.
        for (const state of this.windowByStore.values()){
            clearTimeout(state.timer);
        }
        this.windowByStore.clear();
    }
    logWindowExceeded(storeName, state, windowEnd) {
        try {
            this.config.logger.logTrace(0x1d7dc117 /* tag_352ex */ , TraceLevel.Warning, `Store publish frequency exceeded threshold [Store=${storeName}][PublishCount=${state.count}][WindowStartMs=${state.windowStart}][WindowDurationMs=${windowEnd - state.windowStart}][WindowMs=${this.config.windowMs}][Threshold=${this.config.threshold}][SubscriberCount=${state.lastSubscriberCount}]`);
        } catch  {
        // Never let a telemetry failure break the store publish path.
        }
    }
    /**
     * @param config Monitor configuration. ECS-tunable numbers are sanitized here so a bad rollout
     * value can never turn the diagnostic into every-publish logging or unbounded timestamp
     * retention on the hot path.
     */ constructor(config){
        this.windowByStore = new Map();
        this.config = {
            ...config,
            windowMs: sanitizeNumber(config.windowMs, 1, DefaultStorePublishWindowMs),
            threshold: Math.floor(sanitizeNumber(config.threshold, 1, DefaultStorePublishThreshold))
        };
    }
}
